import { AppContext } from "@lib/types";
import { AuthRoles } from "@lib/types/Auth";

/**
 * Role hierarchy configuration
 * Defines which roles can see/manage which other roles
 */
const ROLE_HIERARCHY = {
  [AuthRoles.SuperAdmin]: [
    AuthRoles.SuperAdmin,
    AuthRoles.Admin,
    AuthRoles.CustomerManager,
    AuthRoles.CustomerAgent,
    AuthRoles.ProviderAgent,
    AuthRoles.UnknownTelegramUser,
  ],
  [AuthRoles.Admin]: [
    AuthRoles.Admin,
    AuthRoles.CustomerManager,
    AuthRoles.CustomerAgent,
    AuthRoles.ProviderAgent,
    AuthRoles.UnknownTelegramUser,
  ],
  [AuthRoles.CustomerManager]: [
    AuthRoles.CustomerManager,
    AuthRoles.CustomerAgent,
  ],
  [AuthRoles.CustomerAgent]: [AuthRoles.CustomerAgent],
  [AuthRoles.ProviderAgent]: [AuthRoles.ProviderAgent],
  [AuthRoles.UnknownTelegramUser]: [AuthRoles.UnknownTelegramUser],
} as const;

/**
 * Get available roles for a given user role
 * @param userRole - The role of the current user
 * @returns Array of roles that the user can see/manage
 */
export const getAvailableRoles = (userRole: AuthRoles): AuthRoles[] => {
  return [...(ROLE_HIERARCHY[userRole] || [])];
};

/**
 * Check if a user can manage a specific role
 * @param userRole - The role of the current user
 * @param targetRole - The role to check if user can manage
 * @returns boolean indicating if user can manage the target role
 */
export const canManageRole = (
  userRole: AuthRoles,
  targetRole: AuthRoles,
): boolean => {
  const availableRoles = getAvailableRoles(userRole);
  return availableRoles.includes(targetRole);
};

/**
 * Get role options for Select component
 * @param userRole - The role of the current user
 * @returns Array of role options for Select component
 */
export const getRoleOptions = (userRole: AuthRoles) => {
  const availableRoles = getAvailableRoles(userRole);

  return availableRoles.map((role) => ({
    label: role,
    text: role,
    value: role,
  }));
};

/**
 * Get role display name with proper formatting
 * @param role - The role to format
 * @returns Formatted role name
 */
export const getRoleDisplayName = (role: AuthRoles): string => {
  switch (role) {
    case AuthRoles.SuperAdmin:
      return "Super Admin";
    case AuthRoles.Admin:
      return "Admin";
    case AuthRoles.CustomerManager:
      return "Customer Manager";
    case AuthRoles.CustomerAgent:
      return "Customer Agent";
    case AuthRoles.ProviderAgent:
      return "Provider Agent";
    case AuthRoles.UnknownTelegramUser:
      return "Unknown Telegram User";
    default:
      return role;
  }
};

/**
 * Get role options with display names for Select component
 * @param userRole - The role of the current user
 * @returns Array of role options with display names for Select component
 */
export const getRoleOptionsWithDisplayNames = (userRole: AuthRoles) => {
  const availableRoles = getAvailableRoles(userRole);

  return availableRoles.map((role) => ({
    label: getRoleDisplayName(role),
    text: getRoleDisplayName(role),
    value: role,
  }));
};

/**
 * Get role options based on context and user role
 * @param userRole - The role of the current user
 * @param context - The context where the modal is being used
 * @returns Array of role options with display names for Select component
 */
export const getRoleOptionsByContext = (
  userRole: AuthRoles,
  context: AppContext,
) => {
  let availableRoles = getAvailableRoles(userRole);

  // Filter roles based on context
  switch (context) {
    case AppContext.CUSTOMER:
      availableRoles = availableRoles.filter(
        (role) => role === AuthRoles.CustomerAgent,
      );
      break;
    case AppContext.PAYMENT_PROVIDER:
      availableRoles = availableRoles.filter(
        (role) => role === AuthRoles.ProviderAgent,
      );
      break;
    default:
      // No additional filtering for general context
      break;
  }

  return availableRoles.map((role) => ({
    label: getRoleDisplayName(role),
    text: getRoleDisplayName(role),
    value: role,
  }));
};
